import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useState } from 'react';

import type { PublicKeyReference } from '@protontech/crypto';
import { CryptoProxy } from '@protontech/crypto';
import { format, isValid } from 'date-fns';
import { c } from 'ttag';

import { API_KEY_SOURCE } from '@proton/shared/lib/constants';
import downloadFile from '@proton/shared/lib/helpers/downloadFile';
import { getKnowledgeBaseUrl } from '@proton/shared/lib/helpers/url';
import { dateLocale } from '@proton/shared/lib/i18n';
import type { ContactPublicKeyModelWithApiKeySource } from '@proton/shared/lib/interfaces/EncryptionPreferences';
import { getFormattedAlgorithmNames } from '@proton/shared/lib/keys/keyAlgorithm';
import { getVerifyingKeys } from '@proton/shared/lib/keys/publicKeys';
import clsx from '@proton/utils/clsx';
import move from '@proton/utils/move';
import uniqueBy from '@proton/utils/uniqueBy';

import { Badge } from '../../../components/badge/Badge';
import DropdownActions from '../../../components/dropdown/DropdownActions';
import ContactKeyWarningIcon from '../../../components/icon/ContactKeyWarningIcon';
import Table from '../../../components/table/Table';
import TableBody from '../../../components/table/TableBody';
import TableRow from '../../../components/table/TableRow';
import KeyVersionBadge from '../../keys/KeyVersionBadge';

interface Props {
    model: ContactPublicKeyModelWithApiKeySource;
    setModel: Dispatch<SetStateAction<ContactPublicKeyModelWithApiKeySource | undefined>>;
    supportV6Keys: boolean;
    setWarnActiveSignerNotPinned: Dispatch<SetStateAction<boolean>>;
}

type LocalKeyModel = {
    publicKey: PublicKeyReference;
    armoredPublicKey: string;
    fingerprint: string;
    algo: string;
    version: number;
    creationTime: Date;
    expirationTime: any;
    isUsedForSending?: boolean;
    isUsedForSendingFallback: boolean;
    isActiveSigner: boolean;
    isWKD: boolean;
    isKOO: boolean;
    isExpired: boolean;
    isRevoked: boolean;
    isTrusted: boolean;
    isObsolete: boolean;
    isCompromised: boolean;
    supportsEncryption: boolean;
    isUploaded: boolean;
    canBeUsedForSending?: boolean;
    canBeTrusted: boolean;
    canBeUntrusted: boolean;
};

/**
 * For internal users, following the v6 key integration, additional usage labels
 * are needed to clarify key usage, and the way key pinning affects it.
 */
const getKeyInternalUsageInfo = ({
    publicKey,
    model,
    encryptionEnabled,
    existsPrimaryKeyV6,
    supportV6Keys,
}: {
    publicKey: PublicKeyReference;
    model: ContactPublicKeyModelWithApiKeySource;
    encryptionEnabled?: boolean;
    existsPrimaryKeyV6: boolean;
    supportV6Keys: boolean;
}) => {
    if (!model.isPGPInternal) {
        // usage is only relevant for internal users
        return { isActiveSigner: false, isUsedForSendingFallback: false };
    }

    const isPrimary = model.primaryKeyFingerprints.has(publicKey.getFingerprint());
    const isV6Key = publicKey.getVersion() === 6;
    const hasTrustedKeys = model.trustedFingerprints.size > 0;
    // the active signer info is only relevant if pinned keys are present,
    // since they affect the keys passed for verification, and might lead to
    // verification errors.
    const isActiveSigner = isPrimary && !isV6Key && hasTrustedKeys;

    if (!encryptionEnabled) {
        // even with e2ee disabled, message verification is still carried out
        return { isActiveSigner, isUsedForSendingFallback: false };
    }

    // if the user has opted-into v6 support, then there is no fallback as the v6 primary key
    // is used for sending by all their clients.
    // similarly, if any key has been pinned, then those are always used for sending.
    const isUsedForSendingFallback = supportV6Keys
        ? false
        : existsPrimaryKeyV6 && isPrimary && !isV6Key && !hasTrustedKeys;

    return { isActiveSigner, isUsedForSendingFallback };
};

// translator: Please translate as in the sentence "this key is the active signer"
export const activeSignerText = c('Key badge').t`Active Signer`;

const ContactKeysTable = ({ model, setModel, supportV6Keys, setWarnActiveSignerNotPinned }: Props) => {
    const [keys, setKeys] = useState<LocalKeyModel[]>([]);

    const { emailAddress } = model;
    const totalApiKeys = model.publicKeys.apiKeys.length;

    // translator: Please translate as in the sentence "this key is the primary one"
    const primaryText = c('Key badge').t`Primary`;
    // translator: Please translate as in the sentence "this key is the fallback one"
    const fallbackText = c('Key badge').t`Fallback`;
    // translator: Please translate as in the sentence "this key is obsolete"
    const obsoleteText = c('Key badge').t`Obsolete`;
    // translator: Please translate as in the sentence "this key is compromised"
    const compromisedText = c('Key badge').t`Compromised`;
    // translator: WKD stands for Web Key Directory (https://wiki.gnupg.org/WKD). You might not need to translate it
    const wkdText = c('Key badge').t`WKD`;
    // translator: KOO stands for keys.openpgp.org. You might not need to translate it
    const kooText = c('Key badge').t`KOO`;
    // translator: Please translate as in the sentence "this key is trusted"
    const trustedText = c('Key badge').t`Trusted`;
    // translator: Please translate as in the sentence "this key is revoked"
    const revokedText = c('Key badge').t`Revoked`;
    // translator: Please translate as in the sentence "this key is expired"
    const expiredText = c('Key badge').t`Expired`;

    /**
     * Extract keys info from model.publicKeys to define table body
     */
    const parse = async () => {
        const allKeys = model.isPGPInternal
            ? [...model.publicKeys.apiKeys]
            : [...model.publicKeys.apiKeys, ...model.publicKeys.pinnedKeys];
        // TODO keys should be compared in full, not just by fingerprint, to avoid collapsing two rows
        // where the e.g. the subkeys have changed, or a key has expired
        const uniqueKeys = uniqueBy(allKeys, (publicKey) => publicKey.getFingerprint());
        // model.encrypt is undefined for internal contacts without pinned keys;
        // encryption status depends on whether mail e2ee is enabled
        const encryptionEnabled = model.isPGPInternal
            ? model.isInternalWithDisabledE2EEForMail === false
            : model.encrypt;
        const existsPrimaryKeyV6 =
            model.isPGPInternal &&
            !!model.publicKeys.apiKeys.find(
                (publicKey) =>
                    publicKey.getVersion() === 6 && model.primaryKeyFingerprints.has(publicKey.getFingerprint())
            );

        const parsedKeys = await Promise.all(
            uniqueKeys.map(async (publicKey, index) => {
                const armoredPublicKey = await CryptoProxy.exportPublicKey({
                    key: publicKey,
                    format: 'armored',
                });
                const fingerprint = publicKey.getFingerprint();
                const creationTime = publicKey.getCreationTime();
                const expirationTime = await publicKey.getExpirationTime();
                const algoInfos = [
                    publicKey.getAlgorithmInfo(),
                    ...publicKey.subkeys.map((subkey) => subkey.getAlgorithmInfo()),
                ];
                const algo = getFormattedAlgorithmNames(algoInfos);
                const version = publicKey.getVersion();
                const isExpired = await CryptoProxy.isExpiredKey({ key: publicKey });
                const isRevoked = await CryptoProxy.isRevokedKey({ key: publicKey });
                const isTrusted = model.trustedFingerprints.has(fingerprint);
                const supportsEncryption = model.encryptionCapableFingerprints.has(fingerprint);
                const isObsolete = model.obsoleteFingerprints.has(fingerprint);
                const isCompromised = model.compromisedFingerprints.has(fingerprint);
                const isUsedForSending =
                    encryptionEnabled && index === 0 && supportsEncryption && !isObsolete && !isCompromised;
                const { isActiveSigner, isUsedForSendingFallback } = getKeyInternalUsageInfo({
                    publicKey,
                    model,
                    encryptionEnabled,
                    existsPrimaryKeyV6,
                    supportV6Keys,
                });
                const isWKD = !!model.apiKeysSourceMap[API_KEY_SOURCE.WKD]?.has(fingerprint);
                const isKOO = !!model.apiKeysSourceMap[API_KEY_SOURCE.KOO]?.has(fingerprint);
                const isUploaded = index >= totalApiKeys;
                const canBeUsedForSending =
                    // the option is only relevant if no API keys are present:
                    // if the key was manually uploaded in the past, but now API keys are also present, the uploaded key can no longer be used for sending
                    // (the user will be prompted to trust one of the API keys when sending)
                    model.isPGPExternalWithoutExternallyFetchedKeys &&
                    !isUsedForSending &&
                    isUploaded &&
                    encryptionEnabled &&
                    supportsEncryption;
                // v6 keys can only be trusted if the user has opted into v6 support because older mobile clients do not support encrypting to these keys
                const canBeTrusted =
                    !isTrusted && !isUploaded && !isCompromised && (publicKey.getVersion() !== 6 || supportV6Keys);
                const canBeUntrusted = isTrusted && !isUploaded;
                return {
                    publicKey,
                    armoredPublicKey,
                    fingerprint,
                    algo,
                    version,
                    creationTime,
                    expirationTime,
                    isUsedForSending,
                    isUsedForSendingFallback,
                    isActiveSigner,
                    isWKD,
                    isKOO,
                    isExpired,
                    isRevoked,
                    isTrusted,
                    supportsEncryption,
                    isObsolete,
                    isCompromised,
                    isUploaded,
                    canBeUsedForSending,
                    canBeTrusted,
                    canBeUntrusted,
                };
            })
        );
        setKeys(parsedKeys);
        setWarnActiveSignerNotPinned(parsedKeys.some(({ isActiveSigner, isTrusted }) => isActiveSigner && !isTrusted));
    };

    useEffect(() => {
        void parse();
    }, [model.publicKeys, model.trustedFingerprints, model.encrypt]);

    return (
        <Table hasActions responsive="cards" responsiveBreakpoint="wide">
            <thead>
                <tr>
                    <th scope="col" className="text-ellipsis w-1/5" title={c('Table header').t`Fingerprint`}>{c(
                        'Table header'
                    ).t`Fingerprint`}</th>
                    <th scope="col" className="text-ellipsis w-1/6" title={c('Table header').t`Created`}>{c(
                        'Table header'
                    ).t`Created`}</th>
                    <th scope="col" className="text-ellipsis w-1/10" title={c('Table header').t`Expires`}>{c(
                        'Table header'
                    ).t`Expires`}</th>
                    <th
                        scope="col"
                        className="text-ellipsis w-custom"
                        style={{ '--w-custom': '16.5em' }}
                        title={c('Table header').t`Type`}
                    >{c('Table header').t`Type`}</th>
                    <th scope="col" className="text-ellipsis w-1/6" title={c('Table header').t`Status`}>{c(
                        'Table header'
                    ).t`Status`}</th>
                    <th
                        scope="col"
                        className={clsx(['text-ellipsis', 'w-custom'])}
                        style={{ '--w-custom': '9em' }}
                        title={c('Table header').t`Actions`}
                    >{c('Table header').t`Actions`}</th>
                </tr>
            </thead>
            <TableBody data-testid="contact-keys-table">
                {keys.map(
                    ({
                        fingerprint,
                        algo,
                        version,
                        creationTime,
                        expirationTime,
                        isUsedForSending,
                        isUsedForSendingFallback,
                        isActiveSigner,
                        isWKD,
                        isKOO,
                        publicKey,
                        armoredPublicKey,
                        isExpired,
                        isRevoked,
                        isTrusted,
                        supportsEncryption,
                        isObsolete,
                        isCompromised,
                        isUploaded,
                        canBeUsedForSending,
                        canBeTrusted,
                        canBeUntrusted,
                    }) => {
                        const creation = new Date(creationTime);
                        const expiration = new Date(expirationTime);
                        const primaryKeyTooltipText = c('PGP Key info')
                            .t`This key is used to encrypt messages to this contact`;
                        const fallbackKeyTooltipText = c('PGP Key info')
                            .t`This key is used to encrypt messages to this contact on older apps`;
                        const activeSignerTooltipText = c('PGP Key info')
                            .t`This contact currently signs messages with this key`;
                        const trustedTooltipText = c('PGP Key info').t`This key is used for message verification`;
                        const untrustKeyText = c('PGP Key info').t`We recommend that you "untrust" this key.`;
                        const obsoleteTooltipText = c('PGP Key info')
                            .t`${emailAddress} has marked this key as obsolete. This key can only be used for signature verification.`;
                        let compromisedTooltipText = c('PGP Key info')
                            .t`${emailAddress} has marked this key as compromised. This key cannot be used neither for encryption nor for signature verification.`;
                        if (isTrusted) {
                            compromisedTooltipText += ' ' + untrustKeyText;
                        }
                        const wkdKeyTooltipText = c('PGP Key info').t`External key automatically fetched via WKD.`;
                        const kooKeyTooltipText = c('PGP Key info')
                            .t`External key automatically fetched from keys.openpgp.org.`;

                        const list = [
                            {
                                text: c('Action').t`Download`,
                                onClick: () => {
                                    const blob = new Blob([armoredPublicKey], {
                                        type: 'text/plain',
                                    });
                                    const filename = `publickey - ${model.emailAddress} - 0x${fingerprint
                                        .slice(0, 8)
                                        .toUpperCase()}.asc`;

                                    downloadFile(blob, filename);
                                },
                            },
                        ];
                        if (canBeUsedForSending) {
                            list.push({
                                text: c('Action').t`Use for sending`,
                                onClick: () => {
                                    const pinnedKeyIndex = model.publicKeys.pinnedKeys.findIndex(
                                        (key) => key.getFingerprint() === fingerprint
                                    );
                                    const reOrderedPinnedKeys =
                                        pinnedKeyIndex !== -1
                                            ? move(model.publicKeys.pinnedKeys, pinnedKeyIndex, 0)
                                            : model.publicKeys.pinnedKeys;
                                    setModel({
                                        ...model,
                                        publicKeys: {
                                            ...model.publicKeys,
                                            pinnedKeys: reOrderedPinnedKeys,
                                            verifyingPinnedKeys: getVerifyingKeys(
                                                reOrderedPinnedKeys,
                                                model.compromisedFingerprints
                                            ),
                                        },
                                    });
                                },
                            });
                        }
                        if (canBeTrusted) {
                            list.push({
                                text: c('Action').t`Trust`,
                                onClick: () => {
                                    const trustedFingerprints = new Set(model.trustedFingerprints);
                                    trustedFingerprints.add(fingerprint);
                                    const trustedKey = model.publicKeys.apiKeys.find(
                                        (key) => key.getFingerprint() === fingerprint
                                    );
                                    if (trustedKey) {
                                        setModel({
                                            ...model,
                                            encrypt: true,
                                            publicKeys: {
                                                ...model.publicKeys,
                                                pinnedKeys: [...model.publicKeys.pinnedKeys, trustedKey],
                                                verifyingPinnedKeys: [
                                                    ...model.publicKeys.verifyingPinnedKeys,
                                                    trustedKey,
                                                ],
                                            },
                                            trustedFingerprints,
                                        });
                                    }
                                },
                            });
                        }

                        if (canBeUntrusted) {
                            list.push({
                                text: c('Action').t`Untrust`,
                                onClick: () => {
                                    const trustedFingerprints = new Set(model.trustedFingerprints);
                                    trustedFingerprints.delete(fingerprint);
                                    const pinnedKeys = model.publicKeys.pinnedKeys.filter(
                                        (key) => key.getFingerprint() !== fingerprint
                                    );
                                    const verifyingPinnedKeys = model.publicKeys.verifyingPinnedKeys.filter(
                                        (key) => key.getFingerprint() !== fingerprint
                                    );
                                    setModel({
                                        ...model,
                                        publicKeys: {
                                            ...model.publicKeys,
                                            pinnedKeys,
                                            verifyingPinnedKeys,
                                        },
                                        trustedFingerprints,
                                    });
                                },
                            });
                        }
                        if (isUploaded) {
                            list.push({
                                text: c('Action').t`Remove`,
                                onClick: () => {
                                    const trustedFingerprints = new Set(model.trustedFingerprints);
                                    const encryptionCapableFingerprints = new Set(model.encryptionCapableFingerprints);
                                    trustedFingerprints.delete(fingerprint);
                                    encryptionCapableFingerprints.delete(fingerprint);
                                    const pinnedKeys = model.publicKeys.pinnedKeys.filter(
                                        (publicKey) => publicKey.getFingerprint() !== fingerprint
                                    );
                                    const hasEncryptionKeys =
                                        model.publicKeys.apiKeys.length > 0 || pinnedKeys.length > 0;

                                    setModel({
                                        ...model,
                                        // If no more encryption keys are available, then we switch off the encryption toggle.
                                        encrypt: hasEncryptionKeys ? model.encrypt : undefined,
                                        trustedFingerprints,
                                        encryptionCapableFingerprints,
                                        publicKeys: {
                                            ...model.publicKeys,
                                            pinnedKeys,
                                            verifyingPinnedKeys: model.publicKeys.verifyingPinnedKeys.filter(
                                                (publicKey) => publicKey.getFingerprint() !== fingerprint
                                            ),
                                        },
                                    });
                                },
                            });
                        }

                        return (
                            <TableRow
                                key={fingerprint}
                                labels={[
                                    c('Title header for keys table').t`Fingerprint`,
                                    c('Title header for keys table').t`Created`,
                                    c('Title header for keys table').t`Expires`,
                                    c('Title header for keys table').t`Type`,
                                    c('Title header for keys table').t`Status`,
                                    c('Title header for keys table').t`Actions`,
                                ]}
                                cells={[
                                    <div key={0} title={fingerprint} className="flex flex-nowrap">
                                        <ContactKeyWarningIcon
                                            className="mr-2 shrink-0 self-center my-auto"
                                            publicKey={publicKey}
                                            emailAddress={model.emailAddress}
                                            isInternal={model.isPGPInternal}
                                            supportsEncryption={supportsEncryption}
                                        />
                                        <span className="flex-1 text-ellipsis">{fingerprint}</span>
                                    </div>,
                                    isValid(creation) ? format(creation, 'PP', { locale: dateLocale }) : '—',
                                    isValid(expiration) ? format(expiration, 'PP', { locale: dateLocale }) : '—',
                                    <div key={1} className="flex flex-row flex-nowrap items-center">
                                        {algo}
                                        <KeyVersionBadge version={version} />
                                    </div>,
                                    <div key={2} className="flex gap-1">
                                        {isUsedForSending ? (
                                            <Badge
                                                type="primary"
                                                data-testid="primary-key-label"
                                                tooltip={primaryKeyTooltipText}
                                            >
                                                {primaryText}
                                            </Badge>
                                        ) : null}
                                        {isUsedForSendingFallback ? (
                                            <Badge
                                                type="success"
                                                data-testid="fallback-key-label"
                                                tooltip={fallbackKeyTooltipText}
                                            >
                                                {fallbackText}
                                            </Badge>
                                        ) : null}
                                        {isActiveSigner ? (
                                            <Badge
                                                type="info"
                                                tooltip={activeSignerTooltipText}
                                                data-testid="active-signer-key-label"
                                            >
                                                {activeSignerText}
                                            </Badge>
                                        ) : null}
                                        {isObsolete && !isCompromised ? (
                                            <Badge
                                                type="warning"
                                                url={getKnowledgeBaseUrl('/download-public-private-key')}
                                                tooltip={obsoleteTooltipText}
                                            >
                                                {obsoleteText}
                                            </Badge>
                                        ) : null}
                                        {isCompromised ? (
                                            <Badge
                                                type="error"
                                                url={getKnowledgeBaseUrl('/download-public-private-key')}
                                                tooltip={compromisedTooltipText}
                                            >
                                                {compromisedText}
                                            </Badge>
                                        ) : null}
                                        {isWKD ? (
                                            <Badge
                                                type="origin"
                                                tooltip={wkdKeyTooltipText}
                                                data-testid="wkd-origin-label"
                                            >
                                                {wkdText}
                                            </Badge>
                                        ) : null}
                                        {isKOO ? (
                                            <Badge
                                                type="origin"
                                                tooltip={kooKeyTooltipText}
                                                data-testid="koo-origin-label"
                                            >
                                                {kooText}
                                            </Badge>
                                        ) : null}
                                        {isTrusted ? (
                                            <Badge type="success" tooltip={trustedTooltipText}>
                                                {trustedText}
                                            </Badge>
                                        ) : null}
                                        {isRevoked ? <Badge type="error">{revokedText}</Badge> : null}
                                        {isExpired ? <Badge type="error">{expiredText}</Badge> : null}
                                    </div>,
                                    <DropdownActions key={fingerprint} size="small" list={list} />,
                                ]}
                            />
                        );
                    }
                )}
            </TableBody>
        </Table>
    );
};

export default ContactKeysTable;
